import { useDisclosure } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { HEADER_ID, PageLayout } from '@/components/templates/PageLayout';
import {
  CmBaseTable,
  CmBox,
  CmCard,
  CmDeleteConfirmModal,
  CmFlex,
  CmHeadingPagination,
  CmLink,
  CmLongLoading,
  CmMessageCard,
  CmScrollBox,
  CmSelect,
  CmTableBodyRow,
  CmTd,
  CmTooltip,
  ReactSingleSelectInput,
  SelectForm,
  useCounter,
} from '@/components/uiParts';
import { LIMIT_PER_PAGE } from '@/domain/models/Pagination';
import { DateDomainService } from '@/domain/services';
import { CurrencyDomainService } from '@/domain/services';
import { useTargetsDomHeight } from '@/hooks/Dom';
import { redirect404Page } from '@/lib/AccessControl';
import { errorHandler } from '@/lib/ErrorHandler';
import { LocalStorageHandler } from '@/lib/LocalStorageHandler';
import { nonNullableStr, ommisionText } from '@/lib/util';
import { HopeMenuManagementJobNotice } from '@/pages/menu-managements/components/Alert/HopeMenuManagementJobNotice';
import CsvUploadBrandItemsModal from '@/pages/menu-managements/components/CsvUploadBrandItemsModal';
import {
  MenuManagementSearchArea,
  SEARCH_AREA_ID,
} from '@/pages/menu-managements/components/SearchAndDrag';
import { useMenuManagementItemForm } from '@/pages/menu-managements/items/hooks';
import { MANAGEMENTITEMLISTACTION_TYPE } from '@/pages/menu-managements/items/service';
import {
  itemTableThs,
  MenuManagementBrandItem,
} from '@/pages/menu-managements/items/service';
import { routes } from '@/router';
import { useAssociateBrandList } from '@/services/AssociateBrand/hooks/list/useAssociateBrandList';
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks';
import { useMenuManagementListBrandItems } from '@/services/BrandItem/hooks/list/useMenuManagementListBrandItems';

import { UberEatsImportBrandItemsModal } from '../components/UberEatsImportBrandItemsModal';

const MenuManagementItemsPage: React.FC = (): JSX.Element => {
  const { isOpen = false, onOpen, onClose } = useDisclosure();
  const [selectItem, setSelectItem] = useState<
    MenuManagementBrandItem | undefined
  >(undefined);
  const history = useHistory();
  const { managerAccountMe } = useManagerAccountMe();
  /* アクセス制御 */
  redirect404Page('menu_management', history, managerAccountMe);
  const [items, setItems] = useState<MenuManagementBrandItem[]>([]);
  const [searchCount, setSearchCount] = useState(0);
  const categoryLength = items.length;
  const [associateBrandSelectOptions, setAssociateBrandSelectOptions] =
    useState<SelectForm[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [timer, setTimer] = useState<number>();
  const [targetAssociateBrandSelect, setTargetAssociateBrandSelect] =
    useState<SelectForm | null>({
      label: '',
      value: '',
    });
  const [isOpenCsvUploadBrandItemsModal, setIsOpenCsvUploadBrandItemsModal] =
    useState(false);
  const [
    isOpenUberEatsImportBrandItemsModal,
    setIsOpenUberEatsImportBrandItemsModal,
  ] = useState(false);

  const { count, decrement, increment, customizeCount } = useCounter(1);
  const [title, setTitle] = useState<string>('');
  const getListQuery = {
    associateBrandId: targetAssociateBrandSelect?.value ?? '',
    currentPage: 1,
    limit: LIMIT_PER_PAGE,
    title: title,
  } as const;

  const handleInputTitle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const title = e.target.value;
    setTitle(title);

    clearTimeout(Number(timer));
    const newTimer = setTimeout(async () => {
      setIsLoading(true);
      const { brand_items: items, count } =
        await useMenuManagementListBrandItems({
          ...getListQuery,
          currentPage: 1,
          title,
        });
      customizeCount(1);
      setSearchCount(count);
      setItems(items);
      setIsLoading(false);
    }, 700);
    setTimer(Number(newTimer));
  };

  const associateBrandSelectInput: ReactSingleSelectInput = {
    value: targetAssociateBrandSelect,
    options: associateBrandSelectOptions,
    onChange: async (associateBrand: SelectForm | null) => {
      if (
        associateBrand === null ||
        associateBrand.value === targetAssociateBrandSelect?.value
      )
        return;
      const associateBrandId = associateBrand.value;
      setIsLoading(true);
      setTargetAssociateBrandSelect(associateBrand);
      const { brand_items: items, count } =
        await useMenuManagementListBrandItems({
          ...getListQuery,
          currentPage: 1,
          associateBrandId,
        });
      customizeCount(1);
      setSearchCount(count);
      setItems(items);
      setIsLoading(false);
    },
    onReset: () => {
      setTargetAssociateBrandSelect({
        value: '',
        label: '',
      });
    },
  };

  const toFirstPage = async () => {
    setIsLoading(true);
    const { brand_items: items } = await useMenuManagementListBrandItems({
      ...getListQuery,
    });
    setItems(items);
    customizeCount(1);
    setIsLoading(false);
  };

  const toPreviousPage = async () => {
    setIsLoading(true);
    const previousPage = count - 1;
    const { brand_items: items } = await useMenuManagementListBrandItems({
      ...getListQuery,
      currentPage: previousPage,
    });
    setItems(items);
    decrement();
    setIsLoading(false);
  };

  const toNextPage = async () => {
    setIsLoading(true);
    const nextPage = count + 1;
    const { brand_items: items } = await useMenuManagementListBrandItems({
      ...getListQuery,
      currentPage: nextPage,
    });
    setItems(items);
    increment();
    setIsLoading(false);
  };

  const toLastPage = async () => {
    setIsLoading(true);
    const lastPage = Math.ceil(searchCount / LIMIT_PER_PAGE);
    const { brand_items: items } = await useMenuManagementListBrandItems({
      ...getListQuery,
      currentPage: lastPage,
    });
    setItems(items);
    customizeCount(lastPage);
    setIsLoading(false);
  };

  const { targetsHeight } = useTargetsDomHeight([HEADER_ID, SEARCH_AREA_ID]);

  const refreshBrandItems = async () => {
    try {
      setIsLoading(true);
      const { brand_items: items, count: searchCount } =
        await useMenuManagementListBrandItems({
          ...getListQuery,
          currentPage: count,
        });
      setSearchCount(searchCount);
      setItems(items);
    } catch (error) {
      errorHandler({ error: error as Error });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const associateBrands = await useAssociateBrandList();
      const associateBrandOptions = associateBrands.map((brand) => {
        return {
          label: brand.name,
          value: brand.id.toString(),
        };
      });

      setAssociateBrandSelectOptions(associateBrandOptions);
      const { associateBrand } = LocalStorageHandler.get('menuManagement');
      const initialAssociateBrand =
        associateBrand !== undefined
          ? associateBrand
          : associateBrandOptions[0];
      LocalStorageHandler.set('menuManagement', {
        associateBrand: initialAssociateBrand,
      });

      const { brand_items: items, count } =
        await useMenuManagementListBrandItems({
          ...getListQuery,
          associateBrandId: initialAssociateBrand.value,
          currentPage: 1,
        });
      setTargetAssociateBrandSelect(initialAssociateBrand);
      customizeCount(1);
      setSearchCount(count);
      setItems(items);
      setIsLoading(false);
    })();
  }, []);

  const getName = (item: MenuManagementBrandItem) => {
    /**
     * TODO: 広報互換性を保つための処理。後で削除する
     * @see { @link https://github.com/tacoms-inc-dev/camel-golang-backend/pull/6240 }
     */
    if (item?.title?.translation !== undefined) {
      return item.title.translation.ja;
    }
    if (item?.name !== undefined && item?.name !== null) {
      return item.name;
    }
    return '';
  };
  const { handleListAction } = useMenuManagementItemForm();

  return (
    <PageLayout
      title={routes.menuManagementItems.title}
      alert={<HopeMenuManagementJobNotice />}
      {...{ associateBrandSelectInput }}
    >
      <CmBox>
        <MenuManagementSearchArea
          targetInputLabel="商品"
          newPagePath={'/menu-managements/items/new'}
          itemValue={title}
          handleClickDownloadCsv={() => setIsOpenCsvUploadBrandItemsModal(true)}
          handleClickUberEatsImportBrandItems={() =>
            setIsOpenUberEatsImportBrandItemsModal(true)
          }
          {...{
            associateBrandSelectInput,
            searchCount,
            handleInputTitleJa: handleInputTitle,
          }}
        />
        {categoryLength > 0 ? (
          <CmCard
            padding="20px"
            height={`calc(100vh - ${targetsHeight}px - 20px)`}
          >
            <CmFlex flexDirection="row-reverse" my="5px">
              <CmHeadingPagination
                totalNum={searchCount}
                currentPage={count}
                LimitPerPage={LIMIT_PER_PAGE}
                {...{ toFirstPage, toNextPage, toPreviousPage, toLastPage }}
              />
            </CmFlex>
            <CmScrollBox>
              <CmBaseTable ths={itemTableThs}>
                {items.map((item, index) => {
                  const memo = item.memo !== null ? item.memo : '';
                  return (
                    <CmTableBodyRow key={index + 1}>
                      <CmTd>
                        <CmLink
                          color="blue.500"
                          to={`/menu-managements/items/${item.id}/edit`}
                        >
                          {getName(item)}
                        </CmLink>
                      </CmTd>
                      <CmTd>{nonNullableStr(item.order_methods)}</CmTd>
                      <CmTd>
                        {/* TODO: image_url !== undefinedは後方互換のため */}
                        {item?.image_url !== null ? '設定あり' : '設定なし'}
                      </CmTd>
                      <CmTd>
                        {item?.takeout_price !== null &&
                          CurrencyDomainService.priceFormat(item.takeout_price)}
                      </CmTd>
                      <CmTd>
                        {item?.eat_in_price !== null &&
                          CurrencyDomainService.priceFormat(item.eat_in_price)}
                      </CmTd>
                      <CmTd>
                        {item?.delivery_price !== null &&
                          CurrencyDomainService.priceFormat(
                            item.delivery_price
                          )}
                      </CmTd>
                      <CmTd>
                        <CmTooltip label={nonNullableStr(item.category_names)}>
                          {/* TODO: undefined checkは後方互換のため */}
                          {ommisionText(
                            nonNullableStr(item?.category_names ?? ''),
                            20
                          )}
                        </CmTooltip>
                      </CmTd>
                      <CmTd>
                        <CmTooltip
                          label={nonNullableStr(item.option_group_names)}
                        >
                          {/* TODO: undefined checkは後方互換のため */}
                          {ommisionText(
                            nonNullableStr(item?.option_group_names ?? ''),
                            20
                          )}
                        </CmTooltip>
                      </CmTd>
                      <CmTd>
                        <CmTooltip label={memo}>
                          {ommisionText(memo, 20)}
                        </CmTooltip>
                      </CmTd>
                      <CmTd textAlign="center">
                        <CmTooltip label={item.updated_at}>
                          {DateDomainService.datetimeFormat(
                            new Date(item.updated_at)
                          )}
                        </CmTooltip>
                      </CmTd>
                      <CmTd textAlign="center">
                        <CmSelect
                          size="sm"
                          value={MANAGEMENTITEMLISTACTION_TYPE.NOSELECT}
                          onChange={async (e) => {
                            if (
                              e.target.value ===
                              MANAGEMENTITEMLISTACTION_TYPE.NOSELECT
                            ) {
                              return;
                            }
                            if (e.target.value === 'DELETE') {
                              setSelectItem(item);
                              onOpen();
                            } else {
                              setIsLoading(true);
                              await handleListAction(
                                item.id,
                                e.target.value as
                                  | 'NOSELECT'
                                  | 'DUPLICATE'
                                  | 'DELETE'
                              );
                              const { brand_items: items, count: searchCount } =
                                await useMenuManagementListBrandItems({
                                  ...getListQuery,
                                  currentPage: count,
                                });
                              setSearchCount(searchCount);
                              setItems(items);
                              setIsLoading(false);
                            }
                          }}
                        >
                          <option
                            value={MANAGEMENTITEMLISTACTION_TYPE.NOSELECT}
                          >
                            アクション
                          </option>
                          <option
                            value={MANAGEMENTITEMLISTACTION_TYPE.DUPLICATE}
                          >
                            複製
                          </option>
                          <option value={MANAGEMENTITEMLISTACTION_TYPE.DELETE}>
                            削除
                          </option>
                        </CmSelect>
                      </CmTd>
                    </CmTableBodyRow>
                  );
                })}
              </CmBaseTable>
            </CmScrollBox>
          </CmCard>
        ) : (
          <>
            {!isLoading && (
              <CmMessageCard margin="100px auto 0">
                検索結果に一致する商品が見当たりませんでした。
              </CmMessageCard>
            )}
          </>
        )}
        {isLoading && <CmLongLoading />}
      </CmBox>
      <CmDeleteConfirmModal
        handleClick={async () => {
          if (selectItem === undefined) {
            return;
          }
          setIsLoading(true);
          await handleListAction(selectItem.id, 'DELETE');
          const { brand_items: items, count: searchCount } =
            await useMenuManagementListBrandItems({
              ...getListQuery,
              currentPage: count,
            });
          setSearchCount(searchCount);
          setItems(items);
          setIsLoading(false);
          onClose();
        }}
        name={`${selectItem?.name}`}
        isOpen={isOpen}
        onClose={onClose}
        amendment={
          <p>また、他から参照されている場合、紐付けが解除されます。</p>
        }
      />
      <CsvUploadBrandItemsModal
        isOpen={isOpenCsvUploadBrandItemsModal}
        handleCloseModal={() => setIsOpenCsvUploadBrandItemsModal(false)}
        associateBrandId={targetAssociateBrandSelect?.value || ''}
        onImportBrandItems={refreshBrandItems}
      />
      <UberEatsImportBrandItemsModal
        associateBrandId={targetAssociateBrandSelect?.value || ''}
        isOpen={isOpenUberEatsImportBrandItemsModal}
        handleCloseModal={() => setIsOpenUberEatsImportBrandItemsModal(false)}
        onImportBrandItems={refreshBrandItems}
      />
    </PageLayout>
  );
};

export default MenuManagementItemsPage;
